import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { settingsPath, type SettingsSectionId } from '~/lib/settings-sections';
import { systemSettingsPath, type SystemSettingsSectionId } from '~/lib/system-settings-sections';

/**
 * FRONT-1938: the one choke point every "Setup" trigger goes through —
 * mirrors `startNewChat`/`openExistingChat` in `app-header.tsx`, which route
 * every "open chat" trigger through `useChatOpenStore` for a similar reason.
 *
 * A plain `navigate(settingsPath(section))` would do the actual opening —
 * Settings has no cross-page id to relay, unlike Chat's session id, since the
 * target section already rides in the URL. What this hook adds on top is the
 * one real piece of cross-store coordination: a chat open-request QUEUED but
 * not yet consumed (Home hasn't mounted to pick it up) has to be cleared, or
 * it sits in `chatOpenStore` and reopens a stale chat the next time the user
 * lands on Home — well after Settings closed and with no visible connection
 * to this click.
 *
 * There is deliberately no reverse-direction "close Settings" store: Settings
 * and every `InboxChatModal` instance already live behind different
 * react-router routes, so navigating from Settings to Chat unmounts the
 * Settings route (and any state it held) before Chat's route ever mounts —
 * an earlier version of this file tracked an `isOpen` flag for that direction
 * too and nothing ever read it (review finding, PR #1379), which is worse
 * than no code: it read as real coordination while doing nothing.
 */
export function useOpenSettings(): (section?: SettingsSectionId) => void {
  const navigate = useNavigate();
  const clearOpenChat = useChatOpenStore((s) => s.clearOpenChat);

  return useCallback(
    (section) => {
      clearOpenChat();
      navigate(section ? settingsPath(section) : '/settings');
    },
    [navigate, clearOpenChat],
  );
}

/**
 * FRONT-2376: the System Settings counterpart to {@link useOpenSettings} —
 * same choke point, same reason (a queued-but-not-yet-consumed chat open
 * request must not sit in `chatOpenStore` and reopen a stale chat once this
 * modal closes and Home is the first thing back on screen).
 */
export function useOpenSystemSettings(): (section?: SystemSettingsSectionId) => void {
  const navigate = useNavigate();
  const clearOpenChat = useChatOpenStore((s) => s.clearOpenChat);

  return useCallback(
    (section) => {
      clearOpenChat();
      navigate(section ? systemSettingsPath(section) : '/system-settings');
    },
    [navigate, clearOpenChat],
  );
}
